import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recordings } from '@/api/recordings';
import type { RecordingRow } from '@/api/recordings';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { AudioPlayer } from '@/components/audio/AudioPlayer';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAuth } from '@/lib/auth-store';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { cn } from '@/lib/cn';
import { formatRef } from '@/lib/formatRef';
import { ChevronLeft, Play, RotateCcw, Trash2 } from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtBytes(raw: string | null): string {
  if (raw == null) return '—';
  const n = Number(raw);
  if (!isFinite(n)) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function timeAgo(iso: string | null): string {
  if (!iso) return '—';
  const diff = Date.now() - new Date(iso).getTime();
  const secs = Math.floor(diff / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function daysUntilPurge(deletedAt: string | null, retentionDays = 90): number | null {
  if (!deletedAt) return null;
  const deletedMs = new Date(deletedAt).getTime();
  const expiresMs = deletedMs + retentionDays * 24 * 3600 * 1000;
  const diffMs = expiresMs - Date.now();
  if (diffMs <= 0) return 0;
  return Math.ceil(diffMs / (24 * 3600 * 1000));
}

// Roles that may restore / purge
const CAN_RESTORE = ['super_admin', 'reseller_owner', 'office_manager'];
// Permanent delete is gated more strictly on the backend (super_admin +
// reseller_owner only); surface the same restriction here so office_manager
// users don't see a button that 403s.
const CAN_HARD_DELETE = ['super_admin', 'reseller_owner'];

// ── Confirm: Permanent delete ────────────────────────────────────────────────

function PermanentDeleteDialog({ row, onClose }: { row: RecordingRow; onClose: () => void }) {
  const qc = useQueryClient();
  const [confirmText, setConfirmText] = useState('');

  const del = useMutation({
    mutationFn: () => recordings.removePermanent(row.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recordings'] });
      onClose();
    },
  });

  const canConfirm = confirmText.trim().toLowerCase() === 'delete';

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Permanently delete recording?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm text-slate-600">
          <p>
            <span className="font-medium text-slate-900">{row.name}</span> will be permanently deleted.
            The audio file will be removed from storage and cannot be recovered.
          </p>
          <div className="rounded-md border border-red-100 bg-red-50 p-3 text-xs text-red-700">
            This cannot be undone. Type <span className="font-mono font-semibold">delete</span> below to confirm.
          </div>
          <input
            autoFocus
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canConfirm && !del.isPending) del.mutate();
            }}
            placeholder="Type 'delete'"
            className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-red-400 focus:outline-none focus:ring-1 focus:ring-red-400"
          />
        </div>
        {del.isError && (
          <p role="alert" className="text-xs text-red-500">
            {del.error instanceof Error ? del.error.message : 'Delete failed.'}
          </p>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={del.isPending}>Cancel</Button>
          </DialogClose>
          <Button
            variant="destructive"
            disabled={!canConfirm || del.isPending}
            onClick={() => del.mutate()}
          >
            {del.isPending ? 'Deleting…' : 'Permanently delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export function RecycleBin() {
  const user = useCurrentUser();
  const accessToken = useAuth((s) => s.accessToken);
  const effectiveCustomerId = useEffectiveCustomerId();
  const qc = useQueryClient();
  const [deleteRow, setDeleteRow] = useState<RecordingRow | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  // Click-to-play: only one row's AudioPlayer is mounted at a time.
  // null = no preview open. Same pattern as the main recordings list,
  // keeps the table tidy until the user actually wants to listen.
  const [playingId, setPlayingId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['recordings', 'deleted', effectiveCustomerId],
    queryFn: () => recordings.list({
      deleted: true,
      limit: 200,
      customer_id: effectiveCustomerId ?? undefined,
    }),
  });

  const rows = data?.rows ?? [];
  const canRestore = CAN_RESTORE.includes(user.role);
  const canHardDelete = CAN_HARD_DELETE.includes(user.role);

  const restore = useMutation({
    mutationFn: (id: string) => recordings.restore(id),
    onSuccess: () => {
      setRestoreError(null);
      qc.invalidateQueries({ queryKey: ['recordings'] });
    },
    onError: (err) => {
      setRestoreError(err instanceof Error ? err.message : 'Restore failed.');
    },
  });

  return (
    <>
      <PageHeader
        title="Recycle Bin"
        description="Deleted recordings are kept for 90 days. Restore them to the main list or permanently delete the file."
        eyebrow={
          <Link
            to="/recordings"
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
            Back to Recordings
          </Link>
        }
      />

      {restoreError && (
        <div
          role="alert"
          className="mb-4 rounded-md border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700"
        >
          {restoreError}
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : error ? (
        <div role="alert" className="text-sm text-red-500">
          {error instanceof Error ? error.message : 'Failed to load recycle bin.'}
          <Button
            variant="outline"
            size="sm"
            className="ms-3"
            onClick={() => qc.invalidateQueries({ queryKey: ['recordings', 'deleted'] })}
          >
            Retry
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-200 p-10 text-center text-sm text-slate-500">
          The recycle bin is empty.
        </div>
      ) : (
        <div className="rounded-md border border-slate-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Ref</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Deleted</TableHead>
                <TableHead>Auto-purge in</TableHead>
                <TableHead className="w-64">Listen</TableHead>
                <TableHead className="w-40 text-end">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const daysLeft = daysUntilPurge(row.deleted_at);
                const urgent = daysLeft != null && daysLeft <= 7;
                return (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium text-slate-700">{row.name}</TableCell>
                    <TableCell className="font-mono text-xs tabular-nums text-slate-500">
                      {formatRef(row.ref_number, 'REC')}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {fmtBytes(row.size_bytes)}
                    </TableCell>
                    <TableCell
                      className="text-xs text-slate-400"
                      title={row.deleted_at ? new Date(row.deleted_at).toLocaleString() : undefined}
                    >
                      {timeAgo(row.deleted_at)}
                    </TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          'inline-block rounded px-1.5 py-0.5 text-xs font-medium tabular-nums leading-none',
                          urgent
                            ? 'bg-amber-50 text-amber-700'
                            : 'bg-slate-100 text-slate-600',
                        )}
                      >
                        {daysLeft == null
                          ? '—'
                          : daysLeft === 0
                            ? 'today'
                            : `${daysLeft} day${daysLeft === 1 ? '' : 's'}`}
                      </span>
                    </TableCell>
                    <TableCell>
                      {/* Inline preview-before-restore. Hidden by default —
                          one Play button per row toggles the AudioPlayer.
                          Soft-deleted recordings stream via the
                          ?include_deleted=1 opt-in (set by audioUrl). The
                          file stays in storage for 90 days so every row in
                          the bin is playable until purge. */}
                      {playingId === row.id ? (
                        <div className="w-60">
                          <AudioPlayer
                            src={recordings.audioUrl(row.id, accessToken, { includeDeleted: true })}
                            downloadName={`REC-${row.ref_number}.wav`}
                            compact
                          />
                        </div>
                      ) : (
                        <button
                          type="button"
                          aria-label={`Play preview of ${row.name}`}
                          onClick={() => setPlayingId(row.id)}
                          className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-900"
                        >
                          <Play className="h-3.5 w-3.5" />
                          Preview
                        </button>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        {canRestore && (
                          <button
                            type="button"
                            aria-label="Restore recording"
                            onClick={() => restore.mutate(row.id)}
                            disabled={restore.isPending && restore.variables === row.id}
                            className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-900 disabled:opacity-50"
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                            Restore
                          </button>
                        )}
                        {canHardDelete && (
                          <button
                            type="button"
                            aria-label="Permanently delete recording"
                            onClick={() => setDeleteRow(row)}
                            className="inline-flex items-center gap-1 rounded border border-transparent px-2 py-1 text-xs font-medium text-red-600 transition-colors hover:bg-red-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {deleteRow && (
        <PermanentDeleteDialog row={deleteRow} onClose={() => setDeleteRow(null)} />
      )}
    </>
  );
}
